import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useOrganizationMessageStatsQuery } from "../../../generated/graphql";
import { formatDateTime } from "../message-content";

const styles = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
    gap: 1,
  },
  card: { p: 1.5 },
  value: { fontSize: 22, fontWeight: 600, lineHeight: 1.2 },
  label: { color: "text.secondary", fontSize: 12 },
  title: { fontWeight: 600 },
} satisfies Record<string, SxProps<Theme>>;

const Stat = ({
  label,
  value,
  color,
}: {
  readonly label: string;
  readonly value: string | number;
  readonly color?: string;
}) => (
  <Paper variant="outlined" sx={styles.card}>
    <Typography sx={styles.value} color={color}>
      {value}
    </Typography>
    <Typography sx={styles.label}>{label}</Typography>
  </Paper>
);

/**
 * Delivery numbers for a sent message. Its own query, so a replica failure
 * shows here and never hides the message or its actions.
 */
const DeliveryStats = ({ uuid }: { readonly uuid: string }) => {
  const { data, error, refetch } = useOrganizationMessageStatsQuery({
    variables: { uuid },
    errorPolicy: "all",
  });
  if (error !== undefined) {
    return (
      <Alert severity="warning">
        Delivery numbers are unavailable right now: {error.message}
      </Alert>
    );
  }
  const stats = data?.organizationMessageStats;
  if (stats === undefined) {
    return null;
  }
  const readPercent =
    stats.deliveredCount === 0
      ? 0
      : Math.round((stats.readCount / stats.deliveredCount) * 100);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={styles.title}>Delivery</Typography>
        <Typography variant="caption" color="text.secondary">
          as of {formatDateTime(stats.asOf)}
        </Typography>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            aria-label="Refresh delivery numbers"
            onClick={() => {
              void refetch();
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box sx={styles.grid}>
        <Stat label="Companies" value={stats.companyCount} />
        <Stat label="Still sending" value={stats.pendingCompanyCount} />
        <Stat
          label="Failed"
          value={stats.failedCompanyCount}
          color={stats.failedCompanyCount > 0 ? "error.main" : undefined}
        />
        <Stat label="In bells" value={stats.deliveredCount} />
        <Stat label={`Read (${readPercent}%)`} value={stats.readCount} />
        <Stat label="Banners closed" value={stats.dismissedCount} />
      </Box>
    </Box>
  );
};

export default DeliveryStats;
